import React, { useCallback, useMemo, useState } from 'react';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useApplyBlackoutDates } from '~/hooks/apis/stopsell/useApplyBlackoutDates';
import { useBlackoutDatesByRoomTypes } from '~/hooks/apis/stopsell/useBlackoutDatesByRoomTypes';
import { useExtractStopsellDates } from '~/hooks/apis/stopsell/useExtractStopsellDates';

import { CatalogRoom, ResolvedRoomMatch, StopsellApplyPayload, StopsellExtraction } from '~/types/stopsell';

import AvailabilityDocumentUploadZone from './AvailabilityDocumentUploadZone';
import AvailabilityRateSelectModal from './AvailabilityRateSelectModal';
import AvailabilityRoomMatchModal from './AvailabilityRoomMatchModal';
import StopsellReviewModal from './StopsellReviewModal';

interface Props {
  propertyId: string;
  roomTypes: Array<App.RoomType>;
  hideHeader?: boolean;
}

export default function StopsellManagementPane({ propertyId, roomTypes, hideHeader = false }: Props) {
  const { mutate, isPending, error } = useExtractStopsellDates();
  const { mutate: applyBlackoutDates, isPending: isApplying } = useApplyBlackoutDates(propertyId);
  const [extractionResult, setExtractionResult] = useState<StopsellExtraction | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [matchOpen, setMatchOpen] = useState(false);
  const [rateSelectOpen, setRateSelectOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedRateIds, setSelectedRateIds] = useState<Record<string, Array<string>> | null>(null);

  const {
    isLoading: isBlackoutLoading,
    isError: isBlackoutError,
    byRoomType: blackoutsByRoomType,
  } = useBlackoutDatesByRoomTypes(propertyId, roomTypes);

  const catalogRooms = useMemo<Array<CatalogRoom>>(
    () =>
      roomTypes.map((r) => ({
        id: r.id,
        name: r.name,
        roomRates: (r.room_rates ?? []).map((rr) => ({ id: rr.id, name: rr.rate_plan.name })),
        blackoutDates: blackoutsByRoomType[r.id] ?? [],
      })),
    [roomTypes, blackoutsByRoomType],
  );

  const catalogRoomsForReview = useMemo<Array<CatalogRoom>>(() => {
    if (!selectedRateIds) return catalogRooms;
    return catalogRooms
      .map((room) => {
        const rateSet = new Set(selectedRateIds[room.id] ?? []);
        return {
          ...room,
          roomRates: room.roomRates.filter((rate) => rateSet.has(rate.id)),
        };
      })
      .filter((room) => room.roomRates.length > 0);
  }, [catalogRooms, selectedRateIds]);

  const extractionForReview = useMemo<StopsellExtraction | null>(() => {
    if (!extractionResult) return null;
    const includedRoomIds = new Set(catalogRoomsForReview.map((r) => r.id));
    return {
      rooms: extractionResult.rooms.filter((room) => room.roomTypeId == null || includedRoomIds.has(room.roomTypeId)),
    };
  }, [extractionResult, catalogRoomsForReview]);

  const handleSelectFile = useCallback(
    (file: File) => {
      setFileName(file.name);
      mutate(
        { file, catalogRooms },
        {
          onSuccess: (data: StopsellExtraction) => {
            setExtractionResult(data);
            setMatchOpen(true);
          },
        },
      );
    },
    [mutate, catalogRooms],
  );

  const handleMatchContinue = useCallback(
    (resolvedRooms: Array<ResolvedRoomMatch>) => {
      if (!extractionResult) return;

      const resolvedExtraction: StopsellExtraction = {
        rooms: extractionResult.rooms.map((room, index) => ({
          ...room,
          roomTypeId: resolvedRooms[index]?.catalogRoomId ?? room.roomTypeId,
        })),
      };

      setExtractionResult(resolvedExtraction);
      setMatchOpen(false);
      setRateSelectOpen(true);
    },
    [extractionResult],
  );

  const handleCloseMatch = useCallback(() => setMatchOpen(false), []);

  const handleRateSelectContinue = useCallback((rates: Record<string, Array<string>>) => {
    setSelectedRateIds(rates);
    setRateSelectOpen(false);
    setReviewOpen(true);
  }, []);

  const handleCloseRateSelect = useCallback(() => setRateSelectOpen(false), []);

  const handleCloseReview = useCallback(() => setReviewOpen(false), []);

  const handleApply = useCallback(
    (payload: StopsellApplyPayload) => {
      applyBlackoutDates(payload, { onSuccess: handleCloseReview });
    },
    [applyBlackoutDates, handleCloseReview],
  );

  return (
    <>
      {!hideHeader && <PageSubheader title="Stopsell Management" />}
      <AvailabilityDocumentUploadZone onSelectFile={handleSelectFile} isPending={isPending} error={error?.message} />

      {extractionResult && (
        <>
          <AvailabilityRoomMatchModal
            key={fileName}
            open={matchOpen}
            onClose={handleCloseMatch}
            onContinue={handleMatchContinue}
            extraction={extractionResult}
            catalogRooms={catalogRooms}
            fileName={fileName}
            isBlackoutLoading={isBlackoutLoading}
            workflowLabel="applying stopsell dates"
          />

          <AvailabilityRateSelectModal
            open={rateSelectOpen}
            onClose={handleCloseRateSelect}
            onContinue={handleRateSelectContinue}
            catalogRooms={catalogRooms}
            fileName={fileName}
          />

          <StopsellReviewModal
            open={reviewOpen}
            onClose={handleCloseReview}
            onApply={handleApply}
            extraction={extractionForReview ?? extractionResult}
            fileName={fileName}
            catalogRooms={catalogRoomsForReview}
            isBlackoutError={isBlackoutError}
            isApplying={isApplying}
          />
        </>
      )}
    </>
  );
}
